import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLoginStore } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { useSearchParams } from '@savgroup-front-common/core/hooks';
import { userManager } from '@savgroup-front-common/core/services/userManager';

import { logError } from '../../../../../configuration';
import { signIn } from '../../../../../domains/login/actions';

export const useOAuthServer = () => {
  const dispatch = useDispatch();
  const setLoginStore = useLoginStore((state) => state.setLoginStore);
  const query = useSearchParams();
  const defaultRedirectUrl = query.get('redirectUrl') || undefined;

  const search = new URLSearchParams(window.location.search);
  const state = search.get('state');

  const { pathname, search: urlSearch } = useLocation();

  const navigate = useNavigate();
  const { user, isAuthenticated, signinRedirect } = useAuth();
  const [isNotRegisteredUser, setIsNotRegisteredUser] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!user && state) {
          await userManager.signinCallback(window.location.href);

          return;
        } else if (user && !isAuthenticated) {
          const urlBeforeRedirection = (user?.state as { redirectUrl?: string })
            ?.redirectUrl;

          if (urlBeforeRedirection && !urlBeforeRedirection?.startsWith('/')) {
            throw new Error('Invalid redirect URL');
          }

          const payload = {
            onLogin: () => navigate(urlBeforeRedirection ?? '/'),
            onGetUserForbidden: () => {
              setIsNotRegisteredUser(true);
            },
          };

          dispatch(signIn({ ...payload, setLoginStore }) as any);

          return;
        }

        window.location.href = defaultRedirectUrl ?? '/';
      } catch (e: any) {
        logError(e);

        if (e.message === 'No matching state found in storage') {
          signinRedirect({
            state: {
              redirectUrl: `${pathname}${urlSearch}`,
            },
          });
        }
      }
    })();
  }, [
    defaultRedirectUrl,
    dispatch,
    signinRedirect,
    navigate,
    pathname,
    urlSearch,
    state,
    user,
    isAuthenticated,
    setLoginStore,
  ]);

  return { isNotRegisteredUser };
};
